// Port of SuiteModel/MapProject.swift's `MapScenes`: Turn Into Scenes… — which documents a map's notes become, in what
// order and under which folders, the same rules. The store's side is `StoreEditor.turnMapNotesIntoScenes`.
import { swiftCompare } from '../model/order.ts';
import { isCFWhitespace } from '../unicode.ts';
import { MapArrange } from './arrange.ts';
import { MapLayout, type MapFrames } from './layout.ts';
import { mapNoteOf, type MapContent } from './map.ts';

/** Along the lines (the centre first, each note followed by what's joined to it), or across the page (rows top to
 *  bottom, each left to right). */
export type MapScenesOrder = 'lines' | 'page';

/** A document to make: the note's first line is its title, the rest its synopsis; a note whose own notes are also chosen
 *  becomes a folder of them (`children` not empty). */
export interface MapScenesItem {
  noteID: string;
  title: string;
  synopsis: string;
  children: MapScenesItem[];
}

/** `Character.isNewline` ("\r\n" is one Character, so it never leaves an empty piece between). */
const NEWLINE = new RegExp('\\r\\n|[\\n\\v\\f\\r' + String.fromCharCode(0x85, 0x2028, 0x2029) + ']');

/** `trimmingCharacters(in: .whitespaces)`. */
function trimWhitespace(s: string): string {
  const cps = [...s];
  let a = 0, b = cps.length;
  while (a < b && isCFWhitespace(cps[a].codePointAt(0)!)) a++;
  while (b > a && isCFWhitespace(cps[b - 1].codePointAt(0)!)) b--;
  return cps.slice(a, b).join('');
}

const tuple = (a: readonly (number | string)[], b: readonly (number | string)[]): number => {
  for (let i = 0; i < Math.min(a.length, b.length); i++) {
    const x = a[i], y = b[i];
    const d = typeof x === 'number' && typeof y === 'number' ? (x < y ? -1 : x > y ? 1 : 0) : swiftCompare(String(x), String(y));
    if (d !== 0) return d;
  }
  return a.length - b.length;
};

export const MapScenes = {
  /** Notes nearer each other than this (vertically) are on one row when reading across the page. */
  rowTolerance: 24,

  isFolder: (item: MapScenesItem) => item.children.length > 0,

  /** A note's words as a title and a synopsis: the first line that has words, and the others joined with spaces. */
  split(text: string): { title: string; synopsis: string } {
    const lines = text.split(NEWLINE).map(trimWhitespace).filter((l) => l !== '');
    return { title: lines[0] ?? '', synopsis: lines.slice(1).join(' ') };
  },

  /** Along the lines when the map has a centre or two of the notes are joined; else across the page. */
  defaultOrder(content: MapContent, selected: ReadonlySet<string>): MapScenesOrder {
    if (content.centre !== null) return 'lines';
    return content.lines.some((l) => selected.has(l.from) && selected.has(l.to)) ? 'lines' : 'page';
  },

  /** The notes that can become documents: chosen, with words, and not already a document's or a character's card. */
  eligible(content: MapContent, selected: ReadonlySet<string>): Set<string> {
    return new Set(content.notes.filter((n) => selected.has(n.id) && n.link === null && MapScenes.split(n.text).title !== '').map((n) => n.id));
  },

  /** The documents to make, as a tree. A note's own notes are the chosen ones stacked under it or one step further out
   *  from it along the lines. */
  plan(content: MapContent, selected: ReadonlySet<string>, order: MapScenesOrder, frames: MapFrames): MapScenesItem[] {
    const chosen = MapScenes.eligible(content, selected);
    if (!chosen.size) return [];
    const stacks = MapArrange.stacks(content);
    const under = new Map<string, string>();
    for (const [head, members] of stacks) for (const m of members) under.set(m, head);
    const lineParents = MapLayout.parents(content);
    const parent = (id: string): string | null => {
      const up = under.get(id) ?? lineParents.get(id);
      return up !== undefined && chosen.has(up) ? up : null;
    };

    // A rank for every note in the chosen order.
    const rank = new Map<string, number>();
    if (order === 'lines') {
      MapLayout.readingOrder(content).forEach((id, i) => rank.set(id, i));
    } else {
      const at = (id: string): [number, number] => {
        const f = frames.get(id), n = mapNoteOf(content, id);
        return [f?.y ?? n?.at.y ?? 0, f?.x ?? n?.at.x ?? 0];
      };
      const sorted = content.notes.map((n) => n.id).sort((a, b) => tuple([...at(a), a], [...at(b), b]));
      const rows: string[][] = [];
      let rowTop = -Infinity;
      for (const id of sorted) {
        if (!rows.length || at(id)[0] >= rowTop + MapScenes.rowTolerance) {
          rows.push([id]);
          rowTop = at(id)[0];
        } else {
          rows[rows.length - 1].push(id);
        }
      }
      let i = 0;
      for (const row of rows) {
        for (const id of [...row].sort((a, b) => tuple([at(a)[1], a], [at(b)[1], b]))) rank.set(id, i++);
      }
    }
    const stackOrder = new Map<string, string>();
    for (const n of content.notes) if (!stackOrder.has(n.id)) stackOrder.set(n.id, n.stackOrder ?? '');
    const before = (a: string, b: string): boolean => {
      // Notes in one stack keep the stack's order.
      const ha = under.get(a);
      if (ha !== undefined && ha === under.get(b)) return tuple([stackOrder.get(a) ?? '', a], [stackOrder.get(b) ?? '', b]) < 0;
      return tuple([rank.get(a) ?? Number.MAX_SAFE_INTEGER, a], [rank.get(b) ?? Number.MAX_SAFE_INTEGER, b]) < 0;
    };
    const byBefore = (a: string, b: string) => (before(a, b) ? -1 : before(b, a) ? 1 : 0);
    const children = new Map<string, string[]>();
    const roots: string[] = [];
    for (const id of chosen) {
      const p = parent(id);
      if (p !== null) children.set(p, [...(children.get(p) ?? []), id]);
      else roots.push(id);
    }
    const item = (id: string): MapScenesItem => {
      const words = MapScenes.split(mapNoteOf(content, id)?.text ?? '');
      return { noteID: id, title: words.title, synopsis: words.synopsis, children: [...(children.get(id) ?? [])].sort(byBefore).map(item) };
    };
    return roots.sort(byBefore).map(item);
  },

  /** How many documents a plan makes. */
  count(items: readonly MapScenesItem[]): number {
    return items.reduce((sum, i) => sum + 1 + MapScenes.count(i.children), 0);
  },
};
